import { useEffect, useState, useCallback } from 'react';

interface ScrollControlOptions {
  smoothScrollSpeed?: number;
  pageHeight?: number;
  snapThreshold?: number;
  totalPages?: number;
}

export const useScrollControl = (options: ScrollControlOptions = {}) => {
  const { 
    smoothScrollSpeed = 0.5, 
    pageHeight = window.innerHeight,
    snapThreshold = 0.1, // 10%でスナップ
    totalPages = 5
  } = options;
  const [currentPage, setCurrentPage] = useState(0);
  const [isScrollLocked, setIsScrollLocked] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [isSnapping, setIsSnapping] = useState(false);

  const lockScroll = useCallback(() => {
    setIsScrollLocked(true);
    document.body.style.overflow = 'hidden';
  }, []);

  const unlockScroll = useCallback(() => {
    setIsScrollLocked(false);
    document.body.style.overflow = 'auto';
  }, []);
  //オールスターゲーム

  const scrollToPage = useCallback((pageIndex: number) => {
    if (pageIndex < 0 || pageIndex >= totalPages || isSnapping) return;
    
    setIsSnapping(true);
    const targetPosition = pageIndex * pageHeight;
    
    // 即座にページを更新
    setCurrentPage(pageIndex);
    
    window.scrollTo({
      top: targetPosition,
      behavior: 'smooth'
    });
    
    // スナップ完了後にフラグをリセット
    setTimeout(() => setIsSnapping(false), 800); // 少し短縮
  }, [pageHeight, totalPages, isSnapping]);

  const smoothScrollTo = useCallback((targetY: number) => {
    const startY = window.scrollY;
    const distance = targetY - startY;
    const duration = Math.abs(distance) * smoothScrollSpeed;
    let startTime: number | null = null;

    const animation = (currentTime: number) => {
      if (startTime === null) startTime = currentTime;
      const timeElapsed = currentTime - startTime;
      const progress = Math.min(timeElapsed / duration, 1);
      
      // Easing function for smooth animation
      const easeInOutCubic = (t: number) => 
        t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1;
      
      const newY = startY + distance * easeInOutCubic(progress);
      window.scrollTo(0, newY);

      if (progress < 1) {
        requestAnimationFrame(animation);
      }
    };

    requestAnimationFrame(animation);
  }, [smoothScrollSpeed]);

  useEffect(() => {
    let scrollTimeout: NodeJS.Timeout;
    let lastScrollY = window.scrollY;
    
    const handleScroll = () => {
      if (isScrollLocked || isSnapping) return;
      
      const currentScrollY = window.scrollY;
      const direction = currentScrollY > lastScrollY ? 1 : -1;
      lastScrollY = currentScrollY;
      
      setScrollPosition(currentScrollY);
      
  const page = Math.floor(currentScrollY / pageHeight);

      setCurrentPage(page);
      
      // スクロールが止まったときにスナップ判定
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        const finalScrollY = window.scrollY;
        const finalPage = Math.floor(finalScrollY / pageHeight);
        const finalPageProgress = (finalScrollY - (finalPage * pageHeight)) / pageHeight;
        // 下方向は10%、上方向は30%でスナップ
        if (finalPageProgress > snapThreshold && direction > 0 && finalPage < totalPages - 1) {
          // 下方向にスナップ（次のページへ）
          scrollToPage(finalPage + 1);
        } else if (finalPageProgress > 0.3 && direction < 0 && finalPage >= 0) {
          // 上方向にスナップ（1ページだけ戻る）
          scrollToPage(finalPage);
        }
      }, 150); // 150ms後にスナップ判定
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, [isScrollLocked, isSnapping, pageHeight, snapThreshold, totalPages, scrollToPage]);

  return {
    currentPage,
    scrollPosition,
    isScrollLocked,
    isSnapping,
    lockScroll,
    unlockScroll,
    scrollToPage,
    smoothScrollTo
  };
};